import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import toast from 'react-hot-toast';

import { ZEN_API_BASE_URL } from '../../config/zen-api';
import { useAuthStore } from '../../store/use-auth-store';
import {
  getActiveTenantId,
  getSessionTenantId,
} from '../../utils/active-tenant';
import { useUserStore } from '../../store/use-user-store';
import {
  isLoggingOutSession,
  resetUnauthorizedHandling,
  shouldHandleUnauthorized,
} from '../../utils/auth-session';
import { clearAuthStoragePreservingPreferences } from '../../utils/storage-utils';
import { getLoginRedirectPath } from '../../utils/soulmap-auth';
import {
  attemptSoulMapSessionRefresh,
  isSoulMapSessionRefreshEligible,
} from '../../utils/soulmap-session-refresh';

const baseURL = ZEN_API_BASE_URL;

let csrfRequest: Promise<string> | null = null;

const getCsrfToken = async (): Promise<string> => {
  const storedToken = sessionStorage.getItem('csrf-token');
  if (storedToken) return storedToken;

  if (!csrfRequest) {
    csrfRequest = axios
      .get<{ csrf_token: string }>(`${baseURL}/auth/csrf/`, {
        withCredentials: true,
      })
      .then(({ data }) => {
        sessionStorage.setItem('csrf-token', data.csrf_token);
        return data.csrf_token;
      })
      .finally(() => {
        csrfRequest = null;
      });
  }

  return csrfRequest;
};

export const storeCsrfToken = (token?: string) => {
  if (token) sessionStorage.setItem('csrf-token', token);
};

export const clearCsrfToken = () => {
  sessionStorage.removeItem('csrf-token');
};

export const AUTH_EVENTS = {
  LOGOUT_REQUIRED: 'LOGOUT_REQUIRED',
} as const;

const dispatchLogoutEvent = () => {
  window.dispatchEvent(new CustomEvent(AUTH_EVENTS.LOGOUT_REQUIRED));
};

function isAuthSessionSetupRequest(url?: string): boolean {
  const path = url || '';
  return (
    path.endsWith('/auth/token-auth/login/') ||
    path.endsWith('/auth/sso-session/')
  );
}

/** Invite / reset APIs are public. A 401 must not be treated as a logged-in session expiry. */
function isPublicAuthApiRequest(url?: string): boolean {
  const path = url || '';
  return (
    path.includes('/auth/password-reset/') ||
    path.includes('/auth/activation/')
  );
}

function isLogoutRequest(url?: string): boolean {
  return (url || '').endsWith('/auth/token-auth/logout/');
}

/**
 * Best-effort server logout before a forced client sign-out. A 401 is not
 * always an expired session (e.g. "Invalid tenant"), and clearing only the
 * client would leave the session cookie usable on a shared computer.
 */
async function endServerSessionQuietly(): Promise<void> {
  const tenantId = getSessionTenantId();
  try {
    await api.post(
      '/auth/token-auth/logout/',
      {},
      {
        headers: tenantId ? { 'x-tenant-id': tenantId.toString() } : undefined,
        skipUnauthorizedRedirect: true,
        suppressErrorToast: true,
        timeout: 5000,
      }
    );
  } catch {
    // Already expired, or unreachable: nothing more to end here.
  }
}

const api = axios.create({
  baseURL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // The logout call itself must reach the server, or the session stays live.
    if (isLoggingOutSession() && !isLogoutRequest(config.url)) {
      return Promise.reject(new axios.CanceledError('Logout in progress'));
    }

    // Public invite/reset APIs must not carry a leftover session cookie: the
    // backend authenticates it and rejects the request as "Invalid tenant".
    // Only effective cross-origin (deployed envs); the backend also ignores
    // cookies on these endpoints, which covers the same-origin dev proxy.
    if (isPublicAuthApiRequest(config.url)) {
      config.withCredentials = false;
    } else if (!config.headers['x-tenant-id']) {
      // This interceptor runs async, so the URL may already have changed (e.g.
      // a redirect to the tenant picker), and some pages name no tenant. A
      // request without a tenant gets 401 "Invalid tenant", which signs a valid
      // user out, so fall back to one of their tenants. Login/SSO setup only
      // uses the URL: stored tenants may belong to the previous session.
      const tenantId = isAuthSessionSetupRequest(config.url)
        ? getActiveTenantId()
        : getSessionTenantId();
      if (tenantId) {
        config.headers['x-tenant-id'] = tenantId.toString();
      }
    }

    const method = config.method?.toLowerCase();
    const path = config.url || '';
    const isSessionSetup =
      path.endsWith('/auth/token-auth/login/') ||
      path.endsWith('/auth/sso-session/');
    if (
      method &&
      !isSessionSetup &&
      !['get', 'head', 'options', 'trace'].includes(method)
    ) {
      config.headers['X-CSRFToken'] = await getCsrfToken();
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    const axiosError = error as AxiosError;
    if (axiosError.response) {
      try {
        switch (axiosError.response.status) {
          case 401: {
            if (
              isAuthSessionSetupRequest(axiosError.config?.url) ||
              isPublicAuthApiRequest(axiosError.config?.url)
            ) {
              break;
            }

            if (axiosError.config?.skipUnauthorizedRedirect) {
              break;
            }

            if (isLoggingOutSession()) {
              break;
            }

            const originalConfig = axiosError.config;
            if (
              originalConfig &&
              !originalConfig._soulMapReauthAttempted &&
              isSoulMapSessionRefreshEligible()
            ) {
              originalConfig._soulMapReauthAttempted = true;
              const refreshed = await attemptSoulMapSessionRefresh();
              if (refreshed) {
                resetUnauthorizedHandling();
                return api(originalConfig);
              }
            }

            if (shouldHandleUnauthorized()) {
              toast.error(
                (axiosError.response?.data as any)?.detail ??
                  'Session expired. Please log in again.'
              );
              await endServerSessionQuietly();
              clearCsrfToken();
              useAuthStore.getState().reset();
              useUserStore.getState().clearUser();
              dispatchLogoutEvent();
              clearAuthStoragePreservingPreferences();
              const loginPath = getLoginRedirectPath(window.location.pathname);
              if (window.location.pathname !== loginPath) {
                window.location.href = loginPath;
              }
            }
            break;
          }
          default:
        }
      } catch (e) {
        //
      }
      //   toast.error(error.response.data.message || "An error occurred");
    } else if (axiosError.request) {
      if (!axiosError.config?.suppressErrorToast) {
        toast.error('No response received from the server');
      }
    } else if (!axiosError.config?.suppressErrorToast) {
      toast.error('Error setting up the request');
    }
    return Promise.reject(error);
  }
);

export default api;
